import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Trophy, Star } from 'lucide-react';

// e.g., import { Badge } from '@/types';
interface Badge {
    name: string;
    display_name: string;
    description: string;
    icon: string;
    color: string;
    category: string;
    earned_at: string;
}

interface BadgeCardProps {
  badge: Badge;
  isNew?: boolean;
  showDescription?: boolean;
  onClick?: (badge: Badge) => void;
}

const BadgeCard: React.FC<BadgeCardProps> = ({ 
  badge, 
  isNew = false, 
  showDescription = true,
  onClick 
}) => {
  const [showDetail, setShowDetail] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick(badge);
    } else {
      setShowDetail(true);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <Card 
        className={`
          relative cursor-pointer transition-all duration-200 hover:shadow-md
          ${isNew ? 'border-green-500 bg-green-50 animate-pulse' : 'border-gray-200 bg-white'}
          hover:scale-105
        `}
        onClick={handleClick}
      >
        <CardContent className="p-4">
          {isNew && (
            <div className="absolute -top-2 -right-2">
              <Badge className="bg-green-500 text-white text-xs px-2 py-1">
                NEW!
              </Badge>
            </div>
          )}
          
          <div className="text-center">
            <div className="text-4xl mb-3">{badge.icon}</div>
            <h4 className="font-semibold text-sm mb-2 text-gray-800">
              {badge.display_name}
            </h4>
            {showDescription && (
              <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                {badge.description}
              </p>
            )}
            
            <div className="flex items-center justify-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: badge.color }}
              />
              <span className="text-xs text-gray-500 capitalize">
                {badge.category}
              </span>
            </div>
            
            <div className="mt-3 text-xs text-gray-400">
              Earned {formatDate(badge.earned_at)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Badge Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Badge Details</DialogTitle>
          </DialogHeader>
          
          <div className="text-center space-y-4">
            <div className="text-6xl mb-4">{badge.icon}</div>
            <h2 className="text-xl font-bold text-gray-800">
              {badge.display_name}
            </h2>
            <p className="text-gray-600">
              {badge.description}
            </p>
            
            <div className="flex items-center justify-center space-x-2">
              <div 
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: badge.color }}
              />
              <span className="text-sm text-gray-500 capitalize">
                {badge.category}
              </span>
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
              <Trophy className="w-4 h-4" />
              <span>Earned {formatDate(badge.earned_at)}</span>
            </div>
            
            <Button 
              onClick={() => setShowDetail(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BadgeCard; 